import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { addDays, differenceInCalendarDays, parseISO } from 'date-fns';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { CalendarBootstrapPayload, CalendarEventModel, CalendarListEntry, CalendarRange } from '@/types/calendar';

interface CalendarStoreState {
  initialized: boolean;
  loading: boolean;
  error: string | null;
  warning: string | null;
  calendars: Record<string, CalendarListEntry>;
  calendarOrder: string[];
  eventsById: Record<string, CalendarEventModel>;
  eventsByCalendar: Record<string, string[]>;
  eventsByDate: Record<string, string[]>;
  visibleCalendarUids: string[];
  useDefaultVisibleCalendars: boolean;
  taskVisibleCalendarUids: string[];
  taskUseDefaultVisibleCalendars: boolean;
  defaultCalendarUids: string[];
  defaultCreateCalendarUid: string | null;
  range: CalendarRange | null;
  timezone: string | null;
  hydrate: (payload: CalendarBootstrapPayload) => void;
  upsertCalendars: (entries: CalendarListEntry[]) => void;
  upsertEvents: (events: CalendarEventModel[], options?: { replace?: boolean }) => void;
  removeEvent: (eventId: string) => void;
  setVisibleCalendars: (calendarUids: string[]) => void;
  toggleCalendarVisibility: (calendarUid: string, visible?: boolean) => void;
  setTaskVisibleCalendars: (calendarUids: string[]) => void;
  toggleTaskCalendarVisibility: (calendarUid: string, visible?: boolean) => void;
  setDefaultCreateCalendar: (calendarUid: string | null) => void;
  setRange: (range: CalendarRange | null) => void;
  setTimezone: (timezone: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (message: string | null) => void;
  setWarning: (message: string | null) => void;
  reset: () => void;
}

type EventIndexResult = {
  eventsByCalendar: Record<string, string[]>;
  eventsByDate: Record<string, string[]>;
};

function normalizeEvent(event: CalendarEventModel): CalendarEventModel {
  const startTimestamp = event.startTimestamp ?? inferTimestamp(event.start?.dateTime, event.start?.date);
  const endTimestamp = event.endTimestamp ?? inferTimestamp(event.end?.dateTime, event.end?.date);
  const startDate = event.startDate ?? deriveDateLabel(event.start?.date, event.start?.dateTime, startTimestamp);
  const endDate = event.endDate ?? deriveDateLabel(event.end?.date, event.end?.dateTime, endTimestamp);
  return {
    ...event,
    startTimestamp,
    endTimestamp,
    startDate,
    endDate,
  };
}

function inferTimestamp(dateTime: string | null | undefined, date: string | null | undefined): number | null {
  if (dateTime) {
    const parsed = Date.parse(dateTime);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (date) {
    const parsed = Date.parse(`${date}T00:00:00Z`);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function deriveDateLabel(date: string | null | undefined, dateTime: string | null | undefined, timestamp: number | null): string | null {
  if (date) {
    return date;
  }
  if (dateTime) {
    const parsed = Date.parse(dateTime);
    if (Number.isFinite(parsed)) {
      return new Date(parsed).toISOString().slice(0, 10);
    }
  }
  if (timestamp && Number.isFinite(timestamp)) {
    return new Date(timestamp).toISOString().slice(0, 10);
  }
  return null;
}

function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const parsed = parseISO(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function expandEventDates(event: CalendarEventModel): string[] {
  const startDate = parseDateOnly(event.startDate ?? event.start?.date ?? null);
  let endDate = parseDateOnly(event.endDate ?? event.end?.date ?? null);
  if (!startDate) {
    return [];
  }
  if (!endDate) {
    endDate = startDate;
  }
  if (event.allDay && event.end?.date) {
    // Google all-day events use exclusive end date, so subtract one day
    const exclusive = parseDateOnly(event.end.date);
    if (exclusive) {
      endDate = addDays(exclusive, -1);
    }
  }
  if (!endDate) {
    endDate = startDate;
  }
  const totalDays = Math.max(0, differenceInCalendarDays(endDate, startDate));
  const dates: string[] = [];
  for (let index = 0; index <= totalDays; index += 1) {
    const cursor = addDays(startDate, index);
    dates.push(cursor.toISOString().slice(0, 10));
  }
  return dates;
}

function buildEventIndexes(events: CalendarEventModel[]): EventIndexResult {
  const eventsByCalendar: Record<string, string[]> = {};
  const eventsByDate: Record<string, string[]> = {};
  const sortedEvents = [...events].sort((a, b) => {
    const startA = a.startTimestamp ?? Number.MAX_SAFE_INTEGER;
    const startB = b.startTimestamp ?? Number.MAX_SAFE_INTEGER;
    if (startA === startB) {
      return (a.summary ?? '').localeCompare(b.summary ?? '');
    }
    return startA - startB;
  });
  const eventsById = sortedEvents.reduce<Record<string, CalendarEventModel>>((acc, event) => {
    acc[event.id] = event;
    return acc;
  }, {});

  sortedEvents.forEach((event) => {
    const calendarUid = event.calendarUid ?? `${event.accountId}:${event.calendarId ?? 'unknown'}`;
    if (!eventsByCalendar[calendarUid]) {
      eventsByCalendar[calendarUid] = [];
    }
    eventsByCalendar[calendarUid].push(event.id);
    expandEventDates(event).forEach((date) => {
      if (!eventsByDate[date]) {
        eventsByDate[date] = [];
      }
      eventsByDate[date].push(event.id);
    });
  });

  Object.keys(eventsByDate).forEach((date) => {
    eventsByDate[date] = eventsByDate[date].sort((a, b) => {
      const eventA = eventsById[a];
      const eventB = eventsById[b];
      const timeA = eventA?.startTimestamp ?? Number.MAX_SAFE_INTEGER;
      const timeB = eventB?.startTimestamp ?? Number.MAX_SAFE_INTEGER;
      if (timeA === timeB) {
        return (eventA?.summary ?? '').localeCompare(eventB?.summary ?? '');
      }
      return timeA - timeB;
    });
  });

  return { eventsByCalendar, eventsByDate };
}

const INITIAL_STATE: Omit<
  CalendarStoreState,
  | 'hydrate'
  | 'upsertCalendars'
  | 'upsertEvents'
  | 'removeEvent'
  | 'setVisibleCalendars'
  | 'toggleCalendarVisibility'
  | 'setTaskVisibleCalendars'
  | 'toggleTaskCalendarVisibility'
  | 'setDefaultCreateCalendar'
  | 'setRange'
  | 'setTimezone'
  | 'setLoading'
  | 'setError'
  | 'setWarning'
  | 'reset'
> &
  Partial<Pick<CalendarStoreState, 'hydrate'>> = {
  initialized: false,
  loading: false,
  error: null,
  warning: null,
  calendars: {},
  calendarOrder: [],
  eventsById: {},
  eventsByCalendar: {},
  eventsByDate: {},
  visibleCalendarUids: [],
  useDefaultVisibleCalendars: true,
  taskVisibleCalendarUids: [],
  taskUseDefaultVisibleCalendars: true,
  defaultCalendarUids: [],
  defaultCreateCalendarUid: null,
  range: null,
  timezone: null,
};

export const useCalendarStore = create<CalendarStoreState>()(
  conditionalDevtools(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,
        hydrate: (payload) => {
          const calendarOrder = payload.calendars.map((calendar) => calendar.uid);
          const calendars = payload.calendars.reduce<Record<string, CalendarListEntry>>((acc, calendar) => {
            acc[calendar.uid] = calendar;
            return acc;
          }, {});
          const normalizedEvents = payload.events.map(normalizeEvent);
          const eventsById = normalizedEvents.reduce<Record<string, CalendarEventModel>>((acc, event) => {
            acc[event.id] = event;
            return acc;
          }, {});
          const { eventsByCalendar, eventsByDate } = buildEventIndexes(normalizedEvents);
          const persistedVisible = get().visibleCalendarUids;
          const persistedUseDefault = get().useDefaultVisibleCalendars ?? true;
          const persistedTaskVisible = get().taskVisibleCalendarUids ?? [];
          const persistedTaskUseDefault = get().taskUseDefaultVisibleCalendars ?? true;
          const defaultVisible = payload.defaultCalendarUids ?? [];
          const useDefaultVisibleCalendars =
            persistedUseDefault && defaultVisible.length > 0 && persistedVisible.length === 0;
          const taskUseDefaultVisibleCalendars =
            persistedTaskUseDefault && defaultVisible.length > 0 && persistedTaskVisible.length === 0;
          set({
            initialized: true,
            loading: false,
            error: null,
            warning: payload.warning ?? null,
            calendars,
            calendarOrder,
            eventsById,
            eventsByCalendar,
            eventsByDate,
            defaultCalendarUids: defaultVisible,
            visibleCalendarUids: useDefaultVisibleCalendars ? defaultVisible : persistedVisible,
            useDefaultVisibleCalendars,
            taskVisibleCalendarUids: taskUseDefaultVisibleCalendars ? defaultVisible : persistedTaskVisible,
            taskUseDefaultVisibleCalendars,
            range: payload.range,
            timezone: payload.timezone ?? null,
          });
        },
        upsertCalendars: (entries) =>
          set((state) => {
            const calendars = { ...state.calendars };
            entries.forEach((entry) => {
              calendars[entry.uid] = entry;
            });
            const calendarOrder = Object.keys(calendars);
            return { calendars, calendarOrder };
          }),
        upsertEvents: (events, options) =>
          set((state) => {
            const replace = options?.replace ?? false;
            const nextEvents = replace ? {} : { ...state.eventsById };
            events.forEach((event) => {
              const normalized = normalizeEvent(event);
              nextEvents[normalized.id] = normalized;
            });
            const nextList = Object.values(nextEvents);
            const indexes = buildEventIndexes(nextList);
            return {
              eventsById: nextEvents,
              eventsByCalendar: indexes.eventsByCalendar,
              eventsByDate: indexes.eventsByDate,
            };
          }),
        removeEvent: (eventId) =>
          set((state) => {
            if (!state.eventsById[eventId]) {
              return state;
            }
            const nextEvents = { ...state.eventsById };
            delete nextEvents[eventId];
            const nextList = Object.values(nextEvents);
            const indexes = buildEventIndexes(nextList);
            return {
              eventsById: nextEvents,
              eventsByCalendar: indexes.eventsByCalendar,
              eventsByDate: indexes.eventsByDate,
            };
          }),
        setVisibleCalendars: (calendarUids) =>
          set({
            visibleCalendarUids: Array.from(new Set(calendarUids)),
            useDefaultVisibleCalendars: false,
          }),
        toggleCalendarVisibility: (calendarUid, visible) =>
          set((state) => {
            const base = state.useDefaultVisibleCalendars ? state.defaultCalendarUids : state.visibleCalendarUids;
            const current = new Set(base);
            const shouldShow = visible ?? !current.has(calendarUid);
            if (shouldShow) {
              current.add(calendarUid);
            } else {
              current.delete(calendarUid);
            }
            return { visibleCalendarUids: Array.from(current), useDefaultVisibleCalendars: false };
          }),
        setTaskVisibleCalendars: (calendarUids) =>
          set({
            taskVisibleCalendarUids: Array.from(new Set(calendarUids)),
            taskUseDefaultVisibleCalendars: false,
          }),
        toggleTaskCalendarVisibility: (calendarUid, visible) =>
          set((state) => {
            const base = state.taskUseDefaultVisibleCalendars ? state.defaultCalendarUids : state.taskVisibleCalendarUids;
            const current = new Set(base);
            const shouldShow = visible ?? !current.has(calendarUid);
            if (shouldShow) {
              current.add(calendarUid);
            } else {
              current.delete(calendarUid);
            }
            return { taskVisibleCalendarUids: Array.from(current), taskUseDefaultVisibleCalendars: false };
          }),
        setDefaultCreateCalendar: (calendarUid) => set({ defaultCreateCalendarUid: calendarUid }),
        setRange: (range) => set({ range }),
        setTimezone: (timezone) => set({ timezone }),
        setLoading: (loading) => set({ loading }),
        setError: (message) => set({ error: message }),
        setWarning: (message) => set({ warning: message }),
        reset: () => set({ ...INITIAL_STATE }),
      }),
      {
        name: 'calendar-storage',
        partialize: (state) => ({
          visibleCalendarUids: state.visibleCalendarUids,
          useDefaultVisibleCalendars: state.useDefaultVisibleCalendars,
          taskVisibleCalendarUids: state.taskVisibleCalendarUids,
          taskUseDefaultVisibleCalendars: state.taskUseDefaultVisibleCalendars,
          defaultCreateCalendarUid: state.defaultCreateCalendarUid,
        }),
      }
    ),
    { name: 'CalendarStore' }
  )
);
